import createDebug from 'debug';
import { AlarmClock, Archive, Layers, Loader2, RefreshCw, Tag, X } from 'lucide-react';
import { useState } from 'react';
import type { CategoryResponse, LinkSummaryResponse, QueueResponse } from '@/api/client';
import { CategorySelector } from '@/components/CategorySelector';
import { useBatchOperationsMutation, useBulkEnrichMutation } from '@/hooks/useLinks';
import { EnrichOptionsModal } from '@/components/EnrichOptionsModal';
import { ErrorDialog } from '@/components/ErrorDialog';
import { QueueSelector } from '@/components/QueueSelector';
import { SnoozeDurationPicker } from '@/components/SnoozeDurationPicker';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

// Enrichment flag values (must match backend)
const EnrichmentFlags = {
	FORCE: 0b1000, // 8
} as const;

const debug = createDebug('app:bulk-ops');

type BulkOperationsProps = {
	selectedLinks: LinkSummaryResponse[];
	queues: QueueResponse[];
	categories: CategoryResponse[];
	onLinksUpdated: (links: LinkSummaryResponse[]) => void;
	onLinksArchived: (linkIds: number[]) => void;
	onClearSelection: () => void;
	className?: string;
};

export function BulkOperationsPanel({
	selectedLinks,
	queues,
	categories,
	onLinksUpdated,
	onLinksArchived,
	onClearSelection,
	className,
}: BulkOperationsProps) {
	const [isArchiving, setIsArchiving] = useState(false);
	const [isEnriching, setIsEnriching] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isSnoozing, setIsSnoozing] = useState(false);
	const [showCategorySelector, setShowCategorySelector] = useState(false);
	const [showQueueSelector, setShowQueueSelector] = useState(false);
	const [showEnrichOptions, setShowEnrichOptions] = useState(false);
	const [showSnoozePicker, setShowSnoozePicker] = useState(false);
	const [error, setError] = useState<{ error: Error | string; details?: string } | null>(null);

	const batchMutation = useBatchOperationsMutation();
	const bulkEnrichMutation = useBulkEnrichMutation();

	async function handleArchiveAll() {
		setIsArchiving(true);
		const ids = selectedLinks.map((l) => l.id);
		debug('archiving %d links via GraphQL batch mutation', ids.length);

		try {
			const result = await batchMutation.mutateAsync({ archive: ids });
			const archivedCount = result?.results?.archived ?? 0;
			debug('archived %d/%d links', archivedCount, ids.length);

			if (result?.errors?.length) {
				setError({
					error: result.errors.map((e: { message: string }) => e.message).join(', '),
					details: `Some links failed to archive`,
				});
			}

			// Report archived IDs (assume all succeeded if count matches)
			onLinksArchived(archivedCount === ids.length ? ids : ids.slice(0, archivedCount));
		} catch (err) {
			debug('batch archive failed: %o', err);
			setError({
				error: err instanceof Error ? err : String(err),
				details: `Failed to archive ${ids.length} links`,
			});
		} finally {
			setIsArchiving(false);
		}
	}

	async function handleCategoryChange(newCategory: string) {
		setIsUpdating(true);
		const ids = selectedLinks.map((l) => l.id);
		debug('changing category to %s for %d links via GraphQL batch mutation', newCategory, ids.length);

		try {
			const result = await batchMutation.mutateAsync({ categorize: { category: newCategory, ids } });
			const updatedCount = result?.results?.categorized ?? 0;
			debug('categorized %d/%d links', updatedCount, ids.length);

			if (result?.errors?.length) {
				setError({
					error: result.errors.map((e: { message: string }) => e.message).join(', '),
					details: `Some links failed to categorize`,
				});
			}

			// Update local state for categorized links
			const updatedLinks = selectedLinks.map((link) => ({
				...link,
				categories: [newCategory],
			}));
			onLinksUpdated(updatedLinks);
		} catch (err) {
			debug('batch categorize failed: %o', err);
			setError({
				error: err instanceof Error ? err : String(err),
				details: `Failed to categorize ${ids.length} links`,
			});
		} finally {
			setIsUpdating(false);
			setShowCategorySelector(false);
		}
	}

	async function handleQueueChange(queueId: number) {
		setIsUpdating(true);
		const ids = selectedLinks.map((l) => l.id);
		debug('changing queue to %d for %d links via GraphQL batch mutation', queueId, ids.length);

		try {
			const result = await batchMutation.mutateAsync({ move: { queueId, ids } });
			const updatedCount = result?.results?.moved ?? 0;
			debug('moved %d/%d links to queue %d', updatedCount, ids.length, queueId);

			if (result?.errors?.length) {
				setError({
					error: result.errors.map((e: { message: string }) => e.message).join(', '),
					details: `Some links failed to move`,
				});
			}

			// Update local state for moved links
			const updatedLinks = selectedLinks.map((link) => ({
				...link,
				queueId,
			}));
			onLinksUpdated(updatedLinks);
		} catch (err) {
			debug('batch move failed: %o', err);
			setError({
				error: err instanceof Error ? err : String(err),
				details: `Failed to move ${ids.length} links`,
			});
		} finally {
			setIsUpdating(false);
			setShowQueueSelector(false);
		}
	}

	async function handleEnrichAll(flags?: number, enrichers?: string[]) {
		setIsEnriching(true);
		const force = flags !== undefined && (flags & EnrichmentFlags.FORCE) !== 0;
		debug('bulk enriching %d links via GraphQL: enrichers=%o, force=%s', selectedLinks.length, enrichers, force);

		try {
			const result = await bulkEnrichMutation.mutateAsync(
				selectedLinks.map((l) => l.id),
				{ enrichers, force }
			);

			debug('bulk enrich result: queued=%d, enricherJobsCreated=%d', result?.queued ?? 0, result?.enricherJobsCreated ?? 0);

			// Mark selected links as "enriching" in the UI
			const updatedLinks = selectedLinks.map((link) => ({
				...link,
				isEnriching: true,
			}));
			onLinksUpdated(updatedLinks);
		} catch (err) {
			debug('bulk enrich failed: %o', err);
			setError({
				error: err instanceof Error ? err : String(err),
				details: `Failed to queue ${selectedLinks.length} links for enrichment`,
			});
		} finally {
			setIsEnriching(false);
		}
	}

	function handleEnrichClick(e: React.MouseEvent) {
		if (e.shiftKey) {
			// Shift+click opens options modal
			setShowEnrichOptions(true);
		} else {
			// Normal click runs standard enrichment
			handleEnrichAll();
		}
	}

	async function handleSnoozeAll(durationSeconds: number) {
		setIsSnoozing(true);
		const ids = selectedLinks.map((l) => l.id);
		debug('snoozing %d links for %d seconds via GraphQL batch mutation', ids.length, durationSeconds);

		try {
			const result = await batchMutation.mutateAsync({ snooze: { duration: durationSeconds, ids } });
			const snoozedCount = result?.results?.snoozed ?? 0;
			debug('snoozed %d/%d links', snoozedCount, ids.length);

			if (result?.errors?.length) {
				setError({
					error: result.errors.map((e: { message: string }) => e.message).join(', '),
					details: `Some links failed to snooze`,
				});
			}

			// Snoozed links disappear from the list (they're hidden), so treat like archive
			onLinksArchived(snoozedCount === ids.length ? ids : ids.slice(0, snoozedCount));
		} catch (err) {
			debug('batch snooze failed: %o', err);
			setError({
				error: err instanceof Error ? err : String(err),
				details: `Failed to snooze ${ids.length} links`,
			});
		} finally {
			setIsSnoozing(false);
		}
	}

	return (
		<div className={cn('flex flex-col h-full border-l bg-background', className)}>
			{/* Header */}
			<div className="flex items-center justify-between border-b px-4 py-3">
				<h2 className="font-semibold">
					{selectedLinks.length} link{selectedLinks.length !== 1 ? 's' : ''} selected
				</h2>
				<Button variant="ghost" size="sm" onClick={onClearSelection}>
					<X className="size-4 mr-1" />
					Clear
				</Button>
			</div>

			{/* Bulk actions - horizontal row */}
			<div className="border-b p-3 flex flex-wrap gap-2">
				<Button
					variant="outline"
					size="sm"
					onClick={() => setShowCategorySelector(true)}
					disabled={isArchiving || isEnriching || isUpdating || isSnoozing}
				>
					{isUpdating && showCategorySelector ? (
						<Loader2 className="size-4 mr-1 animate-spin" />
					) : (
						<Tag className="size-4 mr-1" />
					)}
					Category
				</Button>

				<Button
					variant="outline"
					size="sm"
					onClick={() => setShowQueueSelector(true)}
					disabled={isArchiving || isEnriching || isUpdating || isSnoozing}
				>
					{isUpdating && showQueueSelector ? (
						<Loader2 className="size-4 mr-1 animate-spin" />
					) : (
						<Layers className="size-4 mr-1" />
					)}
					Queue
				</Button>

				<Button
					variant="outline"
					size="sm"
					onClick={handleEnrichClick}
					disabled={isArchiving || isEnriching || isUpdating || isSnoozing}
					title="Click to enrich, Shift+click for options"
				>
					{isEnriching ? (
						<>
							<Loader2 className="size-4 mr-1 animate-spin" />
							Queueing...
						</>
					) : (
						<>
							<RefreshCw className="size-4 mr-1" />
							Enrich
						</>
					)}
				</Button>

				<Button
					variant="outline"
					size="sm"
					onClick={() => setShowSnoozePicker(true)}
					disabled={isArchiving || isEnriching || isUpdating || isSnoozing}
				>
					{isSnoozing ? (
						<>
							<Loader2 className="size-4 mr-1 animate-spin" />
							Snoozing...
						</>
					) : (
						<>
							<AlarmClock className="size-4 mr-1" />
							Snooze
						</>
					)}
				</Button>

				<Button
					variant="destructive"
					size="sm"
					onClick={handleArchiveAll}
					disabled={isArchiving || isEnriching || isUpdating || isSnoozing}
				>
					{isArchiving ? (
						<Loader2 className="size-4 animate-spin" />
					) : (
						<>
							<Archive className="size-4 mr-1" />
							Archive
						</>
					)}
				</Button>
			</div>

			{/* Selected links list */}
			<ScrollArea className="flex-1">
				<div className="p-4 space-y-2">
					{selectedLinks.map((link) => (
						<div
							key={link.id}
							className="text-sm p-2 rounded bg-muted/50 truncate"
							title={link.title}
						>
							{link.title}
						</div>
					))}
				</div>
			</ScrollArea>

			{/* Category Selector Modal */}
			<CategorySelector
				open={showCategorySelector}
				onOpenChange={setShowCategorySelector}
				categories={categories}
				currentCategory={undefined}
				onSelect={handleCategoryChange}
			/>

			{/* Queue Selector Modal */}
			<QueueSelector
				open={showQueueSelector}
				onOpenChange={setShowQueueSelector}
				queues={queues}
				currentQueueId={undefined}
				onSelect={handleQueueChange}
			/>

			{/* Enrich Options Modal */}
			<EnrichOptionsModal
				open={showEnrichOptions}
				onOpenChange={setShowEnrichOptions}
				showEnricherSelection={true}
				onSubmit={(flags, enrichers) => {
					setShowEnrichOptions(false);
					handleEnrichAll(flags, enrichers);
				}}
			/>

			{/* Snooze Duration Picker */}
			<SnoozeDurationPicker
				open={showSnoozePicker}
				onOpenChange={setShowSnoozePicker}
				onSelect={handleSnoozeAll}
				title={`Snooze ${selectedLinks.length} link${selectedLinks.length !== 1 ? 's' : ''}`}
				description="Choose how long to hide these links. They will reappear in your queue after the selected time."
			/>

			{/* Error Dialog */}
			<ErrorDialog
				open={error !== null}
				onOpenChange={(open) => !open && setError(null)}
				title="Bulk Operation Failed"
				error={error?.error ?? null}
				details={error?.details}
			/>
		</div>
	);
}
